import type { IDBOptions } from '../interface/instantiator';
import type { IWorkspaceDB } from '../interface/db';
import type { IModelInstantiator } from '../../model/interface';
import ContainerDB from './db';
import { IDB } from '../../private/db/interface';

interface IControllerDB {
    openCount: number;
    db: IWorkspaceDB;
    open(): Promise<IWorkspaceDB>;
    close(): void;
    isOpen(): boolean;
}

function _open(this: IControllerDB): Promise<IWorkspaceDB> {
    this.openCount += 1;

    if (this.isOpen() && this.openCount > 1) {
        return Promise.resolve(this.db); /* already open */
    }

    const db = this.db;

    return new Promise((resolve, reject) => {
        db.open()
            .then(dbOpen => resolve(dbOpen))
            .catch((err: Error) => reject(err));
    });
}

function _close(this: IControllerDB): void {
    this.openCount = Math.max(0, this.openCount - 1);

    if (this.isOpen() && this.openCount <= 0) {
        this.db.close();
    }
}

function _isOpen(this: IControllerDB): boolean {
    return this.db.isOpen();
}

function _newController(
    modelInstantiator: IModelInstantiator,
    options: IDBOptions,
): Promise<IControllerDB> {
    return new Promise(async function (resolve, reject) {
        try {
            const db = await ContainerDB(modelInstantiator, options);

            const controller: IControllerDB = {
                openCount: 0,
                db,
                open: _open,
                close: _close,
                isOpen: _isOpen,
            };

            resolve(controller);
        } catch (error) {
            reject(error);
        }
    });
}

const records: Record<string, IControllerDB> = {};

function instantiator(
    modelInstantiator: IModelInstantiator,
    options: IDBOptions,
): Promise<IWorkspaceDB> {
    const existing = records[options.name ?? ''];

    if (existing) {
        return Promise.resolve(existing.db);
    }

    return new Promise(async function (resolve, reject) {
        try {
            const controller = await _newController(modelInstantiator, options);
            records[options.name ?? ''] = controller;

            resolve(controller.db);
        } catch (error) {
            reject(error);
        }
    });
}

export default instantiator;
